/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  EventEmitter,
  Input,
  Output,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Params } from '@angular/router';
import { finalize } from 'rxjs/operators';
import { RoutingService } from 'src/app/service/routing.service';
import { UserActivityApiService } from 'src/app/shared/services/customer-api/user-activity-api.service';
import { BreakpointService } from 'src/app/shared/services/breakpoints.service';
import { NewsFeedService } from '../news-feed.service';
import { NewsFeedProcessorService } from '../news-feed-processor.service';
import { NewsfeedDisplaySettingsService } from '../../services/newsfeed-display-settings.service';
import { NewsArticle } from '../../news-newsfeed-interface/news-feed.interfaces';

export interface StoryListItem {
  story: string;
  article: NewsArticle;
  news: NewsArticle[];
  size: number;
  id: string;
  [key: string]: any;
}

export interface StorySelection {
  story: StoryListItem;
  news: NewsArticle;
  type: 'feedstories' | 'feedstories2';
}

@Component({
  selector: 'app-story-view',
  templateUrl: './story-view.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StoryViewComponent {
  @Input() set stories(value: StoryListItem[]) {
    this._stories = value || [];
    this.morenews = [];
    this.loadingMoreStories = [];
  }
  get stories(): StoryListItem[] {
    return this._stories;
  }
  private _stories: StoryListItem[] = [];

  @Input() params: Params = {};
  @Input() newstype: string;
  @Input() fullscreen: boolean;
  @Input() storyContentHeight = 140;

  @Output() storySelected = new EventEmitter<StorySelection>();

  morenews: boolean[] = [];
  loadingMoreStories: boolean[] = [];

  screenSize$ = this.breakpointService.screenSize$;
  displaySettings$ = this.displaySettingsService.getSettings();

  constructor(
    public routing: RoutingService,
    public userActivityApi: UserActivityApiService,
    private breakpointService: BreakpointService,
    private displaySettingsService: NewsfeedDisplaySettingsService,
    private newsFeedService: NewsFeedService,
    private feedProcessor: NewsFeedProcessorService,
    private cdr: ChangeDetectorRef,
    private destroyRef: DestroyRef,
  ) {}

  selectStory(
    story: StoryListItem,
    news: NewsArticle,
    type: 'feedstories' | 'feedstories2',
  ): void {
    this.storySelected.emit({ story, news, type });
  }

  getTeaser(news: NewsArticle): string | undefined {
    return this.feedProcessor.getTeaser(news, this.params);
  }

  checkTimeframe(): boolean {
    return this.feedProcessor.checkTimeframe(this.params);
  }

  getMoreNews(story: StoryListItem, index: number): void {
    if (!story?.id || !this.stories[index]) {
      return;
    }

    const storyHasArticles =
      Array.isArray(this.stories[index].news) &&
      this.stories[index].news.length > 0;

    if (storyHasArticles) {
      this.morenews[index] = !this.morenews[index];
      return;
    }

    if (this.loadingMoreStories[index]) {
      return;
    }

    this.loadingMoreStories[index] = true;

    this.newsFeedService
      .getStoryArticles(story.id)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.loadingMoreStories[index] = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: (articles) => {
          this.stories[index].news = articles;

          if (!articles.length) {
            return;
          }

          articles.forEach((news) =>
            this.feedProcessor.formatNewsTitle(news, this.params),
          );
          this.morenews[index] = true;
          this.cdr.markForCheck();
        },
        error: (error) => {
          console.error(
            `Failed to load additional articles for story ${story.id}`,
            error,
          );
        },
      });
  }
}
